import { Element } from './element';
import { LoggedInUser, User } from '.';

export interface StageState {
  stage: Stage;
  pointer: Pointer | {};
  focus: Focus;
  shouts: Shout[];
  prevStageState: Stage;
  unseenShouts: boolean;
  modifedStage: boolean;
  raisedHands: number[];
  videoState: {
    [key: string]: Array<User['id']>;
  }; //TODO: fix
  laserOffer: {
    isOffered: boolean;
    key: string;
  };
  laserOfferedTo: {
    key?: string;
    user?: LoggedInUser;
    isAccepted: boolean;
  };
}

export enum StageDBType {
  Pointer = 0,
  Stage = 1,
  Shout = 2,
  Focus = 3,
  Video = 4,
}

export interface StageDBInterface<T, E> {
  data: T;
  id: number;
  type: E;
  updatedAt: string;
}

export type StageDBStates = [
  StageDBInterface<Pointer, StageDBType.Pointer>,
  StageDBInterface<Stage, StageDBType.Stage>,
  StageDBInterface<Focus, StageDBType.Focus>,
  StageDBInterface<Video, StageDBType.Video>
];

export enum StageType {
  Pointer = 'st0',
  Stage = 'st1',
  Shout = 'st2',
  Focus = 'st3',
  Video = 'st4',
}

export interface Stage {
  x: number;
  y: number;
  zoom: number;
  width: number;
  height: number;
}
export type StageWithoutDimensions = Omit<Stage, 'width' | 'height'>;

export interface Pointer {
  x: number;
  y: number;
  state: PointerState;
  user?: number;
}

export enum PointerState {
  Down = 0,
  Up = 1,
}

export type Focus = Element | null;

export type Shout = TextShout | RaiseHandShout;

export interface TextShout {
  message: string;
  user: User;
  time: string;
}

export interface RaiseHandShout {
  raised: boolean;
  user: User;
  time: string;
}

export interface Video {
  board_id: number;
  message: string;
  element: number;
  user: number;
}

/* ACTIONS */

export enum StageActionsKeys {
  SET_STAGE = 'SET_STAGE',
  SEND_SHOUT = 'SEND_SHOUT',
  SET_UNSEEN_SHOUTS = 'SET_UNSEEN_SHOUTS',
  SET_VIDEO_STATE = 'SET_VIDEO_STATE',
  OFFER_POINTER = 'OFFER_POINTER',
  ACCEPT_POINTER = 'ACCEPT_POINTER',
  DECLINE_POINTER = 'DECLINE_POINTER',
  TAKEAWAY_POINTER = 'TAKEAWAY_POINTER',
  SOCKET_ON_ST0 = 'SOCKET_ON_ST0',
  SOCKET_ON_ST1 = 'SOCKET_ON_ST1',
  SOCKET_ON_ST2 = 'SOCKET_ON_ST2',
  SOCKET_ON_ST3 = 'SOCKET_ON_ST3',
  SOCKET_ON_ST4 = 'SOCKET_ON_ST4',
  SOCKET_ON_OFFER_LASER = 'SOCKET_ON_OFFER_LASER',
  SOCKET_ON_ACCEPT_LASER = 'SOCKET_ON_ACCEPT_LASER',
  SOCKET_ON_REJECT_LASER = 'SOCKET_ON_REJECT_LASER',
  SOCKET_ON_RECLAIM_LASER = 'SOCKET_ON_RECLAIM_LASER',
  SET_POINTER = 'SET_POINTER',
  SET_FOCUS = 'SET_FOCUS',
  RESET_STATE = 'RESET_STATE',
}

export interface StageActionsPayload {
  [StageActionsKeys.SET_STAGE]: (Stage | StageWithoutDimensions) & { skipEmit?: boolean };
  [StageActionsKeys.SEND_SHOUT]: TextShout['message'];
  [StageActionsKeys.SET_UNSEEN_SHOUTS]: boolean;
  [StageActionsKeys.OFFER_POINTER]: LoggedInUser;
  [StageActionsKeys.ACCEPT_POINTER]: LoggedInUser;
  [StageActionsKeys.DECLINE_POINTER]: LoggedInUser;
  [StageActionsKeys.TAKEAWAY_POINTER]: null;
  [StageActionsKeys.SOCKET_ON_ST0]: Stage;
  [StageActionsKeys.SOCKET_ON_ST1]: Pointer;
  [StageActionsKeys.SOCKET_ON_ST2]: TextShout;
  [StageActionsKeys.SOCKET_ON_ST3]: { element: Focus };
  [StageActionsKeys.SOCKET_ON_ST4]: Video;
  [StageActionsKeys.SOCKET_ON_OFFER_LASER]: {
    key: string;
  };
  [StageActionsKeys.SOCKET_ON_ACCEPT_LASER]: {
    key: string;
    guestOrUser: LoggedInUser;
  };
  [StageActionsKeys.SOCKET_ON_REJECT_LASER]: {
    key: string;
    guestOrUser: LoggedInUser;
  };
  [StageActionsKeys.SOCKET_ON_RECLAIM_LASER]: {
    guestOrUser: LoggedInUser;
  };
  [StageActionsKeys.SET_VIDEO_STATE]: Omit<Video, 'board_id' | 'user'>;
  [StageActionsKeys.SET_POINTER]: Pointer;
  [StageActionsKeys.SET_FOCUS]: { board_element: Focus; withPrevState?: boolean };
  [StageActionsKeys.RESET_STATE]: void;
}

/* MUTATIONS */

export enum StageMutationsKeys {
  SET_STAGE = 'SET_STAGE',
  SET_POINTER = 'SET_POINTER',
  SET_FOCUS = 'SET_FOCUS',
  ADD_SHOUT = 'ADD_SHOUT',
  SET_MODIFY_STAGE = 'SET_MODIFY_STAGE',
  SET_UNSEEN_SHOUTS = 'SET_UNSEEN_SHOUTS',
  SET_RAISED_HAND_USER = 'SET_RAISED_HAND_USER',
  SET_VIDEO_STATE = 'SET_VIDEO_STATE',
  SET_LASER_OFFER = 'SET_LASER_OFFER',
  SET_LASER_OFFERED_TO = 'SET_LASER_OFFERED_TO',
  STAGE_RESET_STATE = 'STAGE_RESET_STATE',
}

export interface StageMutationsPayload {
  [StageMutationsKeys.SET_STAGE]: Stage;
  [StageMutationsKeys.SET_POINTER]: Pointer;
  [StageMutationsKeys.SET_UNSEEN_SHOUTS]: boolean;
  [StageMutationsKeys.SET_RAISED_HAND_USER]: { user: User; raised: boolean };
  [StageMutationsKeys.ADD_SHOUT]: Shout;
  [StageMutationsKeys.SET_MODIFY_STAGE]: boolean;
  [StageMutationsKeys.SET_FOCUS]: { board_element: Focus; withPrevState?: boolean };
  [StageMutationsKeys.SET_VIDEO_STATE]: Video;
  [StageMutationsKeys.SET_LASER_OFFER]: StageState['laserOffer'];
  [StageMutationsKeys.SET_LASER_OFFERED_TO]: StageState['laserOfferedTo'];
  [StageMutationsKeys.STAGE_RESET_STATE]: void;
}

/* GETTERS */

export enum StageGettersKeys {
  stageState = 'stageState',
  pointerState = 'pointerState',
  focusState = 'focusState',
  shouts = 'shouts',
  unseenShouts = 'unseenShouts',
  raisedHand = 'raisedHand',
  modifedStage = 'modifedStage',
  videoState = 'videoState',
  laserOffer = 'laserOffer',
  laserOfferedTo = 'laserOfferedTo',
}

export interface StageGettersReturns {
  [StageGettersKeys.stageState]: Stage;
  [StageGettersKeys.pointerState]: Pointer;
  [StageGettersKeys.focusState]: Focus;
  [StageGettersKeys.unseenShouts]: boolean;
  [StageGettersKeys.modifedStage]: boolean;
  [StageGettersKeys.videoState]: StageState['videoState'];
  [StageGettersKeys.shouts]: Shout[];
  [StageGettersKeys.raisedHand]: number[];
  [StageGettersKeys.laserOffer]: StageState['laserOffer'];
  [StageGettersKeys.laserOfferedTo]: StageState['laserOfferedTo'];
}
